import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save,
  Close,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AttachFile,
  Image,
  VideoFile,
  Description,
} from '@mui/icons-material';
import { createProject, updateProject, uploadProjectLogo, uploadProjectDocument, deleteProjectDocument } from '../../../services/project';
import { getStates, getCities, getBanks } from '../../../services/master';
import { globalMasterService } from '../../../services/stateAPI';
import AlertModal from '../../../components/AlertModal';
import ConfirmModal from '../../../components/ConfirmModal';

const ProjectForm = () => {
  const history = useHistory();
  const location = useLocation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const [amenitiesOptions, setAmenitiesOptions] = useState([]);
  const [commissionOptions, setCommissionOptions] = useState([]);
  const [progressOptions, setProgressOptions] = useState([]);
  const [loadingProgressOptions, setLoadingProgressOptions] = useState(false);
  const [projectTypeOptions, setProjectTypeOptions] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [banks, setBanks] = useState([]);
  const [authorityOptions, setAuthorityOptions] = useState([]);
  const [projectLogoFile, setProjectLogoFile] = useState(null);
  const [projectLogoPreview, setProjectLogoPreview] = useState('');

  // Document management states
  const [documents, setDocuments] = useState([]);
  const [documentForm, setDocumentForm] = useState(getEmptyDocumentForm());
  const [editingDocumentId, setEditingDocumentId] = useState(null);

  // Add city dialog states
  const [addCityDialogOpen, setAddCityDialogOpen] = useState(false);
  const [newCityName, setNewCityName] = useState("");
  const [addingCity, setAddingCity] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [accountError, setAccountError] = useState("");
  const [ifscError, setIfscError] = useState("");
  const [errors, setErrors] = useState({
    projectName: '',
    developer: '',
    type: '',
    totalInventory: '',
    contactPerson: '',
    phone: '',
    progressType: '',
    startDate: '',
    commissionMethod: '',
    commission: '',
    state: '',
    city: '',
  });

  // Modal states
  const [alertConfig, setAlertConfig] = useState({ open: false, title: 'Alert', message: '', type: 'info' });
  const [documentDeleteId, setDocumentDeleteId] = useState(null);

  function getEmptyDocumentForm() {
    return {
      date: '',
      documentType: '',
      documentTitle: '',
      file: null,
      documentDescription: '',
      existingServerId: '',
      existingFileName: '',
      existingFileKey: '',
      existingFileViewUrl: '',
      existingFileType: '',
    };
  }

  const formatDocumentDate = (dateValue) => {
    if (!dateValue) return '';

    try {
      return new Date(dateValue).toISOString().split('T')[0];
    } catch (error) {
      return dateValue;
    }
  };

  const showAlert = (message, type = 'info', title = 'Alert') => {
    setAlertConfig({ open: true, title, message, type });
  };

  const validateMobile = (mobile) => {
    if (!mobile) return false;
    const mobileRegex = /^[6-9]\d{9}$/;
    return mobileRegex.test(mobile);
  };

  const validateIFSC = (ifsc) => {
    if (!ifsc) return true; // Optional field
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    return ifscRegex.test(ifsc.toUpperCase());
  };

  const validateAccount = (account) => {
    if (!account) return true; // Optional field
    const accountRegex = /^\d{9,18}$/;
    return accountRegex.test(account);
  };

  const ensureArray = (amenities) => {
    if (!amenities) return [];
    if (Array.isArray(amenities)) return amenities;
    if (typeof amenities === 'string') {
      return amenities.split(',').map(item => item.trim()).filter(item => item !== '');
    }
    return [];
  };

  const normalizeType = (rawType) => {
    if (!rawType) return 'Residential';
    const match = Object.keys({
      Plot: true,
      Apartment: true,
      RowHouse: true,
      Villa: true,
      Commercial: true,
      Residential: true,
    }).find((key) => key.toLowerCase() === String(rawType).toLowerCase());
    return match || 'Residential';
  };

  const typeFieldConfig = {
    Plot: { key: 'totalPlot', label: 'Total Plots', placeholder: 'Enter total plots' },
    Apartment: { key: 'totalBuilding', label: 'Total Buildings', placeholder: 'Enter total buildings' },
    RowHouse: { key: 'totalRowHouses', label: 'Total Row Houses', placeholder: 'Enter total row houses' },
    Villa: { key: 'totalVillas', label: 'Total Villas', placeholder: 'Enter total villas' },
    Commercial: { key: 'totalUnits', label: 'Total Buildings', placeholder: 'Enter total buildings' },
    Residential: { key: 'totalUnits', label: 'Total Units', placeholder: 'Enter total units' },
  };

  const [formData, setFormData] = useState({
    _id: '',
    projectId: '',
    badge: 'Villa',
    projectName: '',
    prefix: '',
    developer: '',
    type: '',
    status: 'Active',
    progressType: '',
    // commission: '',
    commissionMethod: 'level', // Default to Project Commission
    // levelCommission: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    startDate: '',
    completeDate: '',
    contactPerson: '',
    reraNo: '',
    totalPlot: '',
    length: '',
    width: '',
    totalBuilding: '',
    totalRowHouses: '',
    totalVillas: '',
    totalUnits: '',
    guidelineValue: '',
    latitude: '',
    longitude: '',
    description: '',
    projectLogo: '',
    address: '',
    bankName: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    amenities: [],
    totalBuilding: '',
    rewardPoints: '',
    authority: '',
  });

  useEffect(() => {
    if (location.state && location.state.project) {
      setIsEditMode(true);
      const project = location.state.project;
      console.log('project', project);
      setSelectedProject(project);

      // Extract progressType and commission from API response
      const progressTypeValue = project.progressType || '';
      const commissionValue = project.commission !== undefined && project.commission !== null ? String(project.commission) : '';

      console.log('Edit mode - progressType:', progressTypeValue, 'commission:', commissionValue);

      setFormData({
        _id: project._id || '',
        projectId: project.projectId || '',
        badge: project.badge || 'Villa',
        projectName: project.projectName || '',
        developer: project.developerName || project.developer || '',
        type: normalizeType(project.projectType || project.type),
        status: project.status || 'Active',
        // progressType: progressTypeValue,
        progressType: progressTypeValue,
        commission: commissionValue,
        commissionMethod: project.commissionMethod || 'project', // Default to Project Commission
        email: project.contact?.email || project.email || '',
        phone: project.contact?.phone || project.phone || '',
        city: project.address?.city || project.city || '',
        state: project.address?.state || project.state || '',
        startDate: project.projectDetails?.startDate ? new Date(project.projectDetails.startDate).toISOString().split('T')[0] : project.startDate || '',
        completeDate: project.projectDetails?.completionDate ? new Date(project.projectDetails.completionDate).toISOString().split('T')[0] : project.completeDate || '',
        contactPerson: project.contact?.person || project.contactPerson || '',
        reraNo: project.projectDetails?.reraNumber || project.reraNo || '',
        totalPlot: project.projectDetails?.totalInventory || project.totalPlot || '',
        length: project.projectDetails?.length || project.length || '',
        width: project.projectDetails?.width || project.width || '',
        totalBuilding: project.projectDetails?.totalInventory || project.totalBuilding || '',
        totalRowHouses: project.projectDetails?.totalInventory || project.totalRowHouses || '',
        totalVillas: project.projectDetails?.totalInventory || project.totalVillas || '',
        totalUnits: project.projectDetails?.totalInventory || project.totalUnits || '',
        guidelineValue: project.projectDetails?.guidelineValue || project.guidelineValue || '',
        latitude: project.projectDetails?.latitude || project.latitude || '',
        longitude: project.projectDetails?.longitude || project.longitude || '',
        description: project.projectDetails?.description || project.description || '',
        projectLogo: project.projectLogo || project.logo || '',
        address: project.address?.fullAddress || project.address || '',
        bankName: project.bankDetails?.bankName || project.bankName || '',
        accountHolderName: project.bankDetails?.accountHolderName || project.accountHolderName || '',
        accountNumber: project.bankDetails?.accountNumber || project.accountNumber || '',
        ifscCode: project.bankDetails?.ifscCode || project.ifscCode || '',
        amenities: ensureArray(project.amenities),
        totalBuilding: project.projectDetails?.totalInventory || project.totalBuilding || '',
        rewardPoints: project?.rewardPoints || 0,
        authority: project?.authority || '',
      });
      setDocuments(
        Array.isArray(project.documents)
          ? project.documents.map((doc, index) => ({
            id: doc._id || doc.id || `${project._id || 'project'}-doc-${index}`,
            date: formatDocumentDate(doc.date),
            type: doc.type || doc.mode || 'document',
            mode: doc.mode || doc.type || 'document',
            title: doc.title || '',
            description: doc.description || '',
            file: null,
            serverId: doc._id || doc.serverId || '',
            isPersisted: Boolean(doc._id || doc.serverId || doc.fileKey || doc.fileUrl),
            fileName: doc.fileName || '',
            fileKey: doc.fileKey || doc.fileUrl || '',
            fileViewUrl: doc.fileViewUrl || doc.viewUrl || '',
            fileType: doc.fileType || '',
          }))
          : []
      );
      setDocumentForm(getEmptyDocumentForm());
      setEditingDocumentId(null);
      setProjectLogoFile(null);
      setProjectLogoPreview(project.projectLogoViewUrl || project.logoViewUrl || project.projectLogo || project.logo || '');
    } else {
      setIsEditMode(false);
      setSelectedProject(null);
      setFormData({
        _id: '',
        projectId: '',
        badge: 'Villa',
        projectName: '',
        developer: '',
        type: '',
        status: 'Active',
        // progressType: '',
        progressType: '',
        commission: '',
        commissionMethod: 'level', // Default to Project Commission
        amenities: [],
        email: '',
        phone: '',
        city: '',
        state: '',
        startDate: '',
        completeDate: '',
        contactPerson: '',
        reraNo: '',
        totalPlot: '',
        length: '',
        width: '',
        totalBuilding: '',
        totalRowHouses: '',
        totalVillas: '',
        totalUnits: '',
        guidelineValue: '',
        latitude: '',
        longitude: '',
        description: '',
        projectLogo: '',
        address: '',
        bankName: '',
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
        totalBuilding: '',
        rewardPoints: '',
        authority: '',
      });
      setDocuments([]);
      setDocumentForm(getEmptyDocumentForm());
      setEditingDocumentId(null);
      setProjectLogoFile(null);
      setProjectLogoPreview('');
    }
  }, [location.state]);

  // Scroll to top when in edit mode
  useEffect(() => {
    if (isEditMode) {
      // Scroll to top immediately
      window.scrollTo({
        top: 0,
        behavior: 'auto'
      });

      // Also scroll after a short delay to ensure DOM is fully rendered
      const timeoutId = setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isEditMode]);

  // Fetch amenities from API
  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/globalMasterDetails?type=amenities`,
        );
        const body = await response.json().catch(() => ({}));
        const list = Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body?.details)
            ? body.details
            : Array.isArray(body)
              ? body
              : [];
        // Filter only active amenities (status: true)
        const activeList = list.filter((item) => item?.status === true);
        const names = activeList
          .map((item) =>
            item?.details?.amenitiesName ||
            item?.details?.name ||
            item?.amenitiesName ||
            item?.name ||
            item?.value ||
            item?.label ||
            '',
          )
          .filter((name) => name && name.trim().length > 0);
        setAmenitiesOptions(Array.from(new Set(names)).sort());
      } catch (error) {
        console.error('Failed to fetch amenities', error);
        setAmenitiesOptions([]);
      }
    };

    fetchAmenities();
  }, []);

  // Fetch commission options from API
  useEffect(() => {
    const fetchCommission = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/globalMasterDetails?type=commission`,
        );
        const body = await response.json().catch(() => ({}));
        const list = Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body?.details)
            ? body.details
            : Array.isArray(body)
              ? body
              : [];
        const percentages = list
          .map((item) => {
            const percentage = item?.details?.commissionPercentage || item?.commissionPercentage || item?.value || item?.label || '';
            return percentage;
          })
          .filter((percentage) => percentage !== '' && percentage !== null && percentage !== undefined)
          .map((p) => String(p));
        setCommissionOptions(Array.from(new Set(percentages)).sort((a, b) => parseFloat(a) - parseFloat(b)));
      } catch (error) {
        console.error('Failed to fetch commission', error);
        setCommissionOptions([]);
      }
    };

    fetchCommission();
  }, []);

  // Fetch progress options from API
  useEffect(() => {
    const fetchProgress = async () => {
      setLoadingProgressOptions(true);
      try {
        const progressData = await globalMasterService.getProjectProgress();
        const progressNames = progressData.map(item => item.value);
        setProgressOptions(Array.from(new Set(progressNames)).sort());
      } catch (error) {
        console.error('Failed to fetch project progress', error);
        // Fallback options if API fails
        setProgressOptions(['Planning', 'Foundation', 'Construction', 'Finishing', 'Completed']);
      } finally {
        setLoadingProgressOptions(false);
      }
    };

    fetchProgress();
  }, []);

  // Fetch project type options from API
  useEffect(() => {
    const fetchProjectTypes = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/globalMasterDetails?type=projectType`,
        );
        const body = await response.json().catch(() => ({}));
        const list = Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body?.details)
            ? body.details
            : Array.isArray(body)
              ? body
              : [];
        const projectTypes = list
          .filter((item) => item.status === true && !item.isDeleted)
          .map((item) => {
            const projectType = item?.details?.projectType || item?.projectType || item?.name || item?.value || item?.label || '';
            return projectType;
          })
          .filter((type) => type && type.trim().length > 0);
        setProjectTypeOptions(Array.from(new Set(projectTypes)).sort());
      } catch (error) {
        console.error('Failed to fetch project types', error);
        setProjectTypeOptions([]);
      }
    };

    fetchProjectTypes();
  }, []);

  // Fetch states from API
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const fetchedStates = await getStates();
        setStates(fetchedStates);
      } catch (error) {
        console.error('Failed to fetch states', error);
        setStates([]);
      }
    };
    fetchStates();
  }, []);

  // Fetch cities from API
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const fetchedCities = await getCities();
        setCities(fetchedCities);
      } catch (error) {
        console.error('Failed to fetch cities', error);
        setCities([]);
      }
    };
    fetchCities();
  }, []);

  // Fetch banks from API
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const fetchedBanks = await getBanks();
        setBanks(fetchedBanks);
      } catch (error) {
        console.error('Failed to fetch banks', error);
        setBanks([]);
      }
    };
    fetchBanks();
  }, []);

  // Fetch authority options
  useEffect(() => {
    const fetchAuthorities = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/globalMasterDetails?type=authority_charges`,
        );
        const body = await response.json().catch(() => ({}));
        const list = Array.isArray(body?.data) ? body.data : [];
        const authoritiesMap = new Map();

        list.forEach((item) => {
          if (item.status === true && item.details?.authorityName) {
            authoritiesMap.set(item.details.authorityName, {
              name: item.details.authorityName,
              charge: item.details.perSqFtCharge || 0
            });
          }
        });

        setAuthorityOptions(Array.from(authoritiesMap.values()));
      } catch (error) {
        console.error('Failed to fetch authorities', error);
      }
    };
    fetchAuthorities();
  }, []);

  // Filter cities based on selected state
  const filteredCities = cities.filter(city =>
    !formData.state || city.state === formData.state
  );

  // Document handling functions
  const handleDocumentChange = (field, value) => {
    setDocumentForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setDocumentForm(prev => ({
        ...prev,
        file: file
      }));
    }
  };

  const handleSaveDocument = () => {
    if (!documentForm.date || !documentForm.documentType || !documentForm.documentTitle || (!documentForm.file && !documentForm.existingFileKey)) {
      showAlert('Please fill in all required fields', 'warning', 'Warning');
      return;
    }

    const newDocument = {
      id: editingDocumentId || Date.now(),
      date: documentForm.date,
      type: documentForm.documentType,
      mode: documentForm.documentType, // Using type as mode as requested
      title: documentForm.documentTitle,
      description: documentForm.documentDescription,
      file: documentForm.file,
      serverId: documentForm.existingServerId || '',
      isPersisted: Boolean(documentForm.existingServerId || documentForm.existingFileKey),
      fileName: documentForm.file ? documentForm.file.name : documentForm.existingFileName,
      fileKey: documentForm.existingFileKey || '',
      fileViewUrl: documentForm.existingFileViewUrl || '',
      fileType: documentForm.file?.type || documentForm.existingFileType || '',
    };

    if (editingDocumentId) {
      setDocuments(prev => prev.map(doc =>
        doc.id === editingDocumentId ? newDocument : doc
      ));
    } else {
      setDocuments(prev => [...prev, newDocument]);
    }

    // Reset form
    setDocumentForm(getEmptyDocumentForm());
    setEditingDocumentId(null);
  };

  const handleEditDocument = (document) => {
    setDocumentForm({
      date: document.date,
      documentType: document.type,
      documentTitle: document.title,
      file: null,
      documentDescription: document.description,
      existingServerId: document.serverId || '',
      existingFileName: document.fileName || '',
      existingFileKey: document.fileKey || document.fileUrl || '',
      existingFileViewUrl: document.fileViewUrl || '',
      existingFileType: document.fileType || '',
    });
    setEditingDocumentId(document.id);
  };

  const handleDeleteDocument = (documentId) => {
    setDocumentDeleteId(documentId);
  };

  const confirmDeleteDocument = async () => {
    if (documentDeleteId === null || documentDeleteId === undefined) {
      setDocumentDeleteId(null);
      return;
    }

    const targetDocument = documents.find((doc) => doc.id === documentDeleteId);

    if (!targetDocument) {
      setDocumentDeleteId(null);
      return;
    }

    try {
      if (isEditMode && formData._id && targetDocument.serverId) {
        await deleteProjectDocument(formData._id, targetDocument.serverId);
      }

      setDocuments((prev) => prev.filter((doc) => doc.id !== documentDeleteId));

      if (editingDocumentId === documentDeleteId) {
        setDocumentForm(getEmptyDocumentForm());
        setEditingDocumentId(null);
      }

      setDocumentDeleteId(null);
      showAlert('Document removed successfully!', 'success', 'Success');
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to remove document.';
      setDocumentDeleteId(null);
      showAlert(message, 'error', 'Error');
    }
  };

  const handleCloseAddCityDialog = () => {
    setAddCityDialogOpen(false);
    setNewCityName("");
  };

  const handleAddCity = async () => {
    if (!newCityName.trim() || !formData.state) {
      return;
    }

    setAddingCity(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/globalMasterDetails`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "city",
          details: {
            cityName: newCityName.trim(),
            state: formData.state,
          },
          status: true,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to add city");
      }

      const result = await response.json();

      const newCity = {
        id: result.data?._id || Date.now().toString(),
        name: newCityName.trim(),
        value: newCityName.trim(),
        state: formData.state,
      };

      setCities((prev) => [...prev, newCity]);
      setFormData((prev) => ({ ...prev, city: newCity.value }));
      handleCloseAddCityDialog();
    } catch (error) {
      console.error("Error adding city:", error);
      showAlert("Failed to add city. Please try again.", "error", "Error");
    } finally {
      setAddingCity(false);
    }
  };


  const validateField = (field, value) => {
    let errorMsg = '';
    switch (field) {
      case 'projectName':
        if (!value || !value.trim()) errorMsg = 'Project Name is required';
        break;
      case 'developer':
        if (!value || !value.trim()) errorMsg = 'Developer Name is required';
        break;
      case 'type':
        if (!value) errorMsg = 'Project Type is required';
        break;
      case 'totalInventory': {
        const config = typeFieldConfig[formData.type];
        const val = config ? formData[config.key] : '';
        if (!val || val === '' || parseInt(val) <= 0) {
          const label = config ? config.label : 'Inventory';
          errorMsg = `${label} must be greater than 0`;
        }
        break;
      }
      case 'phone':
        if (!value) {
          errorMsg = 'Phone number is required';
        } else if (value.length < 10) {
          errorMsg = 'Phone number must be 10 digits';
        } else if (!validateMobile(value)) {
          errorMsg = 'Mobile number invalid (10 digits starting with 6-9)';
        }
        break;
      case 'startDate':
        if (!value) errorMsg = 'Start Date is required';
        break;
      case 'commissionMethod':
        if (!value) errorMsg = 'Commission Method is required';
        break;
      case 'commission':
        if (formData.commissionMethod === 'project' && !value) {
          errorMsg = 'Commission is required';
        }
        break;
      case 'state':
        if (!value) errorMsg = 'State is required';
        break;
      case 'city':
        if (!value) errorMsg = 'City is required';
        break;
      default:
        break;
    }
    return errorMsg;
  };

  const handleFieldBlur = (field) => {
    let val = formData[field];
    if (field === 'totalInventory') {
      const config = typeFieldConfig[formData.type];
      val = config ? formData[config.key] : '';
    }
    const errorMsg = validateField(field, val);
    setErrors((prev) => ({ ...prev, [field]: errorMsg }));
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      // Reset city when state changes
      if (field === 'state') {
        updated.city = '';
      }
      return updated;
    });

    // Clear error dynamically when they start typing
    setErrors((prev) => {
      const updated = { ...prev };
      if (field === 'state') {
        updated.city = '';
      }
      const errorMsg = validateField(field, value);
      updated[field] = errorMsg;

      // Special case: if project type changes, clear totalInventory validation error
      if (field === 'type') {
        updated.totalInventory = '';
      }
      return updated;
    });
  };

  const handleTypeChange = (value) => {
    const normalized = normalizeType(value);
    setFormData((prev) => {
      const next = { ...prev, type: normalized };
      const activeKey = typeFieldConfig[normalized]?.key;

      Object.values(typeFieldConfig).forEach(({ key }) => {
        if (key !== activeKey) {
          next[key] = '';
        }
      });
      return next;
    });
  };

  const handleFormSubmit = async () => {
    // Populate errors object for high-lighting invalid fields
    const newErrors = {
      projectName: validateField('projectName', formData.projectName),
      developer: validateField('developer', formData.developer),
      type: validateField('type', formData.type),
      totalInventory: validateField('totalInventory', ''),
      contactPerson: validateField('contactPerson', formData.contactPerson),
      phone: validateField('phone', formData.phone),
      progressType: validateField('progressType', formData.progressType),
      startDate: validateField('startDate', formData.startDate),
      commissionMethod: validateField('commissionMethod', formData.commissionMethod),
      commission: validateField('commission', formData.commission),
      state: validateField('state', formData.state),
      city: validateField('city', formData.city),
    };
    setErrors(newErrors);

    // Get the total inventory field key based on project type
    const config = typeFieldConfig[formData.type];
    const totalInventoryKey = config ? config.key : null;
    const totalInventoryValue = totalInventoryKey ? formData[totalInventoryKey] : '';

    // Validate all required fields
    const missingFields = [];
    if (!formData.projectName) missingFields.push('Project Name');
    if (!formData.type) missingFields.push('Project Type');
    if (!formData.state) missingFields.push('State');
    if (!formData.city) missingFields.push('City');
    if (!totalInventoryValue || totalInventoryValue === '' || parseInt(totalInventoryValue) <= 0) {
      const fieldLabel = config ? config.label : 'Total Inventory';
      missingFields.push(fieldLabel);
    }
    if (!formData.contactPerson) missingFields.push('Contact Person Name');
    if (!formData.phone || !validateMobile(formData.phone)) {
      missingFields.push('Valid Mobile Number (10 digits starting with 6-9)');
      setPhoneError(formData.phone ? "Mobile number invalid" : "Phone number is required");
    }
    if (!formData.progressType) missingFields.push('Project Progress');
    if (!formData.startDate) missingFields.push('Start Date');
    if (!formData.commissionMethod) missingFields.push('Commission Method');
    if (formData.commissionMethod === 'project' && !formData.commission) missingFields.push('Commission (%)');

    // Bank Details Validation
    if (formData.accountNumber && !validateAccount(formData.accountNumber)) {
      missingFields.push('Valid Account Number (9-18 digits)');
      setAccountError("Account number must be between 9 and 18 digits");
    } else {
      setAccountError("");
    }

    if (formData.ifscCode && !validateIFSC(formData.ifscCode)) {
      missingFields.push('Valid IFSC Code');
      setIfscError("Invalid IFSC format (e.g. ABCD0123456)");
    } else {
      setIfscError("");
    }

    if (missingFields.length > 0) {
      showAlert(`Please fill in all required fields:\n${missingFields.join(', ')}`, 'warning', 'Required Fields');
      
      const fieldOrder = [
        'projectName',
        'developer',
        'type',
        'totalInventory',
        'progressType',
        'contactPerson',
        'phone',
        'startDate',
        'commissionMethod',
        'commission',
        'state',
        'city'
      ];
      
      const firstErrorField = fieldOrder.find(field => newErrors[field]);
      if (firstErrorField) {
        setTimeout(() => {
          const element = document.getElementById(`field-${firstErrorField}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }
        }, 100);
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
      return;
    }

    try {
      let uploadedProjectLogo = formData.projectLogo || '';
      const uploadedDocuments = await Promise.all(
        documents.map(async (doc) => {
          let fileKey = doc.fileKey || '';
          let fileName = doc.fileName || '';
          let fileType = doc.fileType || '';

          if (doc.file instanceof File) {
            const uploadResult = await uploadProjectDocument(doc.file);
            fileKey = uploadResult.key || '';
            fileName = doc.file.name || fileName;
            fileType = doc.file.type || fileType;
          }

          return {
            date: doc.date || '',
            type: doc.type || 'document',
            mode: doc.mode || doc.type || 'document',
            title: doc.title || '',
            description: doc.description || '',
            fileKey,
            fileName,
            fileType,
          };
        })
      );

      if (projectLogoFile) {
        const uploadResult = await uploadProjectLogo(projectLogoFile);
        uploadedProjectLogo = uploadResult.key || '';
      }

      if (isEditMode) {
        // Get the total inventory based on project type
        const config = typeFieldConfig[formData.type];
        const totalInventory = config ? parseInt(formData[config.key]) || 0 : 0;

        // Transform formData to match API structure for update
        const updatePayload = {
          projectName: formData.projectName,
          developerName: formData.developer, // Use developerName instead of developer
          prefix: formData.prefix || '',
          projectType: formData.type,
          progressType: formData.progressType || '',
          commissionMethod: formData.commissionMethod || '',
          ...(formData.commissionMethod === 'project' && {
            commission: formData.commission ? parseFloat(formData.commission) : 0,
          }),
          ...(formData.commissionMethod === 'level' && {
            levelCommission: 'level',
          }),
          amenities: formData.amenities || [],
          contact: {
            person: formData.contactPerson || '',
            email: formData.email || '',
            phone: formData.phone || '',
          },
          projectDetails: {
            startDate: formData.startDate || null,
            completionDate: formData.completeDate || null,
            reraNumber: formData.reraNo || '',
            totalInventory: totalInventory,
            guidelineValue: parseFloat(formData.guidelineValue) || 0,
            latitude: formData.latitude || '',
            longitude: formData.longitude || '',
            description: formData.description || '',
          },
          address: {
            fullAddress: formData.address || '',
            city: formData.city || '',
            state: formData.state || '',
          },
          bankDetails: {
            bankName: formData.bankName || '',
            accountHolderName: formData.accountHolderName || '',
            accountNumber: formData.accountNumber || '',
            ifscCode: formData.ifscCode || '',
          },
          documents: uploadedDocuments,
          projectLogo: uploadedProjectLogo,
          rewardPoints: formData.rewardPoints ? parseInt(formData.rewardPoints) || 0 : 0,
          authority: formData.type === 'Plot' ? formData.authority : '',
          // length: formData.type === 'Plot' ? parseFloat(formData.length) || 0 : 0,
          // width: formData.type === 'Plot' ? parseFloat(formData.width) || 0 : 0,
        };

        console.log('Project Update Payload:', updatePayload);

        await updateProject(formData._id, updatePayload);
        showAlert(`Project ${selectedProject.projectId} updated successfully!`, 'success', 'Success');
      } else {
        // Get the total inventory based on project type
        const config = typeFieldConfig[formData.type];
        const totalInventory = config ? parseInt(formData[config.key]) || 0 : 0;

        // Transform formData to match API structure
        const payload = {
          projectName: formData.projectName,
          developerName: formData.developer,
          prefix: formData.prefix || '',
          projectType: formData.type,
          progressType: formData.progressType || '',
          commissionMethod: formData.commissionMethod || '',
          ...(formData.commissionMethod === 'project' && {
            commission: formData.commission ? parseFloat(formData.commission) : 0,
          }),
          ...(formData.commissionMethod === 'level' && {
            levelCommission: 'level',
          }),
          amenities: formData.amenities || [],
          contact: {
            person: formData.contactPerson || '',
            email: formData.email || '',
            phone: formData.phone || '',
          },
          projectDetails: {
            startDate: formData.startDate || null,
            completionDate: formData.completeDate || null,
            reraNumber: formData.reraNo || '',
            totalInventory: totalInventory,
            guidelineValue: parseFloat(formData.guidelineValue) || 0,
            latitude: formData.latitude || '',
            longitude: formData.longitude || '',
            description: formData.description || '',
          },
          address: {
            fullAddress: formData.address || '',
            city: formData.city || '',
            state: formData.state || '',
          },
          bankDetails: {
            bankName: formData.bankName || '',
            accountHolderName: formData.accountHolderName || '',
            accountNumber: formData.accountNumber || '',
            ifscCode: formData.ifscCode || '',
          },
          documents: uploadedDocuments,
          projectLogo: uploadedProjectLogo,
          rewardPoints: formData.rewardPoints ? parseInt(formData.rewardPoints) || 0 : 0,
          authority: formData.type === 'Plot' ? formData.authority : '',
          length: formData.type === 'Plot' ? parseFloat(formData.length) || 0 : 0,
          width: formData.type === 'Plot' ? parseFloat(formData.width) || 0 : 0,
        };

        await createProject(payload);
        showAlert('New project added successfully!', 'success', 'Success');
      }
      setTimeout(() => {
        history.push('/app/project/ProjectList');
      }, 1500);
    } catch (error) {
      console.error('Error submitting project:', error);
      showAlert('Error submitting project. Please try again.', 'error', 'Error');
    }
  };

  const handleFormClose = () => {
    history.push('/app/project/ProjectList');
  };

  const renderAmenitiesValue = (selected) => {
    const selectedArray = Array.isArray(selected) ? selected : [];

    return (
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        py: 0.25
      }}>
        {selectedArray.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: '#95a5a6',
              fontStyle: 'italic',
              fontSize: '0.75rem'
            }}
          >
            No amenities selected
          </Typography>
        ) : (
          selectedArray.map((value) => (
            <Chip
              key={value}
              label={value}
              size="small"
              sx={{
                backgroundColor: '#E8F4F8',
                color: '#2C5F7F',
                fontWeight: 400,
                fontSize: '0.7rem',
                height: 22,
                '& .MuiChip-label': {
                  px: 0.75,
                  py: 0
                },
                border: '1px solid #D4E9F2',
                '&:hover': {
                  backgroundColor: '#D4E9F2'
                }
              }}
            />
          ))
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 4, pb: 3, borderBottom: '2px solid #e0e0e0', backgroundColor: '#fff', p: 3, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              {isEditMode ? 'Edit Project' : 'Add New Project'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditMode
                ? `Update project information for ${selectedProject?.projectId}`
                : 'Fill in the details to add a new project'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Close />}
            onClick={handleFormClose}
            sx={{ textTransform: 'none', px: 3 }}
          >
            Back to List
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Basic Information
            </Typography>
          </Grid>
          {/* <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Project ID"
              value={formData.projectId}
              onChange={(e) => handleFormChange('projectId', e.target.value)}
              placeholder="PRJ001"
            />
          </Grid> */}
          <Grid item xs={12} sm={6} md={3} id="field-projectName">
            <TextField
              fullWidth
              required
              label="Project Name"
              value={formData.projectName}
              onChange={(e) => handleFormChange('projectName', e.target.value)}
              onBlur={() => handleFieldBlur('projectName')}
              error={!!errors.projectName}
              helperText={errors.projectName}
              placeholder="Enter project name"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} id="field-developer">
            <TextField
              fullWidth
              required
              label="Developer Name"
              value={formData.developer}
              onChange={(e) => handleFormChange('developer', e.target.value)}
              onBlur={() => handleFieldBlur('developer')}
              error={!!errors.developer}
              helperText={errors.developer}
              placeholder="Developer company name"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} id="field-type">
            <FormControl fullWidth required error={!!errors.type}>
              <InputLabel>Project Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => {
                  handleTypeChange(e.target.value);
                  setErrors((prev) => ({ ...prev, type: '' }));
                }}
                label="Project Type"
                required
                onBlur={() => handleFieldBlur('type')}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {projectTypeOptions.length > 0 ? (
                  projectTypeOptions.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))
                ) : (
                  // Fallback to default options if API hasn't loaded yet
                  <>
                    <MenuItem value="Residential">Residential</MenuItem>
                    <MenuItem value="Commercial">Commercial</MenuItem>
                    <MenuItem value="Plot">Plot</MenuItem>
                    <MenuItem value="Villa">Villa</MenuItem>
                    <MenuItem value="Apartment">Apartment</MenuItem>
                    <MenuItem value="RowHouse">RowHouse</MenuItem>
                  </>
                )}
              </Select>
              {errors.type && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.type}
                </Typography>
              )}
            </FormControl>
          </Grid>
          {(() => {
            const config = typeFieldConfig[formData.type] || null;
            if (!config) return null;
            return (
              <Grid item xs={12} sm={6} md={3} id="field-totalInventory">
                <TextField
                  fullWidth
                  required
                  label={config.label}
                  type="number"
                  disabled={isEditMode}
                  value={formData[config.key] || ''}
                  onBlur={() => handleFieldBlur('totalInventory')}
                  error={!!errors.totalInventory}
                  helperText={errors.totalInventory}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Only allow positive numbers (non-zero and non-negative)
                    // Allow empty string for clearing, or numbers greater than 0
                    // Limit to 4 digits maximum
                    if (val === '') {
                      handleFormChange(config.key, val);
                    } else if (!isNaN(val) && parseFloat(val) > 0) {
                      // Check if the number has more than 4 digits
                      const numStr = val.toString().replace(/\./g, '');
                      if (numStr.length <= 4) {
                        handleFormChange(config.key, val);
                      }
                    } else if (val === '0') {
                      // Prevent setting to 0
                      handleFormChange(config.key, '');
                    }
                  }}
                  onKeyDown={(e) => {
                    // Prevent negative sign, 'e', 'E', '+'
                    if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                      e.preventDefault();
                    }
                    // Prevent typing if already at 4 digits (excluding decimal point)
                    const currentValue = formData[config.key] || '';
                    const numStr = currentValue.toString().replace(/\./g, '');
                    if (numStr.length >= 4 && /[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  placeholder={config.placeholder}
                  inputProps={{ min: 1, max: 9999, maxLength: 4 }}
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              </Grid>
            );
          })()}



          {/* <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleFormChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Under Construction">Under Construction</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="On Hold">On Hold</MenuItem>
              </Select>
            </FormControl>
          </Grid> */}
          {/* <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Badge Type</InputLabel>
              <Select
                value={formData.badge}
                onChange={(e) => handleFormChange('badge', e.target.value)}
                label="Badge Type"
              >
                <MenuItem value="Villa">Villa</MenuItem>
                <MenuItem value="Plot">Plot</MenuItem>
                <MenuItem value="Apartment">Apartment</MenuItem>
                <MenuItem value="Commercial">Commercial</MenuItem>
                <MenuItem value="Residential">Residential</MenuItem>
              </Select>
            </FormControl>
          </Grid> */}


          <Grid item xs={12} sm={6} md={3} id="field-progressType">
            <FormControl fullWidth required error={!!errors.progressType}>
              <InputLabel>Project Progress Type</InputLabel>
              <Select
                value={formData.progressType || ''}
                onChange={(e) => {
                  handleFormChange('progressType', e.target.value);
                  setErrors((prev) => ({ ...prev, progressType: '' }));
                }}
                label="Progress Type"
                required
                disabled={loadingProgressOptions}
                onBlur={() => handleFieldBlur('progressType')}
              >
                <MenuItem value="">
                  {loadingProgressOptions ? 'Loading...' : 'Select progress type'}
                </MenuItem>
                {progressOptions.map((projectProgress) => (
                  <MenuItem key={projectProgress} value={projectProgress}>
                    {projectProgress}
                  </MenuItem>
                ))}
              </Select>
              {errors.progressType && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.progressType}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Plot Length and Width fields - Show only when Plot type is selected */}
          {formData.type === 'Plot' && !isEditMode && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Plot Length (ft)"
                  type="number"
                  value={formData.length}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Allow positive numbers and decimals
                    if (val === '' || (!isNaN(val) && parseFloat(val) >= 0)) {
                      handleFormChange('length', val);
                    }
                  }}
                  onKeyDown={(e) => {
                    // Prevent negative sign
                    if (e.key === '-') {
                      e.preventDefault();
                    }
                  }}
                  placeholder="Enter plot length"
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Plot Width (ft)"
                  type="number"
                  value={formData.width}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Allow positive numbers and decimals
                    if (val === '' || (!isNaN(val) && parseFloat(val) >= 0)) {
                      handleFormChange('width', val);
                    }
                  }}
                  onKeyDown={(e) => {
                    // Prevent negative sign
                    if (e.key === '-') {
                      e.preventDefault();
                    }
                  }}
                  placeholder="Enter plot width"
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              </Grid>
            </>
          )}


          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Prefix"
              value={formData.prefix}
              onChange={(e) => handleFormChange('prefix', e.target.value)}
              placeholder="Enter prefix (e.g., PRJ, SITE)"
              helperText="Optional: Prefix for generating plot/unit IDs"
            />
          </Grid>
          {formData.type === 'Plot' && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Authority</InputLabel>
                <Select
                  value={formData.authority || ''}
                  onChange={(e) => handleFormChange('authority', e.target.value)}
                  label="Authority"
                >
                  <MenuItem value="">
                    <em>Select Authority</em>
                  </MenuItem>
                  {authorityOptions.map((auth) => (
                    <MenuItem key={auth.name} value={auth.name}>
                      {auth.name} (₹{auth.charge})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {/* <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Badge Type</InputLabel>
              <Select
                value={formData.badge}
                onChange={(e) => handleFormChange('badge', e.target.value)}
                label="Badge Type"
              >
                <MenuItem value="Villa">Villa</MenuItem>
                <MenuItem value="Plot">Plot</MenuItem>
                <MenuItem value="Apartment">Apartment</MenuItem>
              </Select>
            </FormControl>
          </Grid> */}





          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Contact Information
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3} id="field-contactPerson">
            <TextField
              fullWidth
              required
              label="Contact Person"
              value={formData.contactPerson}
              onChange={(e) => handleFormChange('contactPerson', e.target.value)}
              onBlur={() => handleFieldBlur('contactPerson')}
              error={!!errors.contactPerson}
              helperText={errors.contactPerson}
              placeholder="Enter contact person name"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              placeholder="email@example.com"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} id="field-phone">
            <TextField
              fullWidth
              required
              label="Phone Number"
              type="tel"
              value={formData.phone}
              onChange={(e) => {
                const val = e.target.value;
                const digitsOnly = val.replace(/\D/g, '').slice(0, 10);
                handleFormChange('phone', digitsOnly);

                let errorMsg = '';
                if (digitsOnly && digitsOnly.length < 10) {
                  errorMsg = "Phone number must be 10 digits";
                } else if (digitsOnly && !validateMobile(digitsOnly)) {
                  errorMsg = "Mobile number invalid";
                }
                setErrors((prev) => ({ ...prev, phone: errorMsg }));
              }}
              onBlur={() => {
                const val = formData.phone;
                let errorMsg = '';
                if (!val) {
                  errorMsg = "Phone number is required";
                } else if (val.length < 10) {
                  errorMsg = "Phone number must be 10 digits";
                } else if (!validateMobile(val)) {
                  errorMsg = "Mobile number invalid";
                }
                setErrors((prev) => ({ ...prev, phone: errorMsg }));
              }}
              onKeyDown={(e) => {
                if (!/[0-9]/.test(e.key) &&
                  !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key) &&
                  !(e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                }
              }}
              placeholder="9876543210"
              inputProps={{ maxLength: 10, inputMode: 'numeric' }}
              helperText={errors.phone || ""}
              error={!!errors.phone}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Project Logo (optional)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                size="small"
                sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              >
                Upload Logo
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    if (!file) {
                      setProjectLogoFile(null);
                      setProjectLogoPreview('');
                      handleFormChange('projectLogo', '');
                      return;
                    }
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setProjectLogoFile(file);
                      setProjectLogoPreview(reader.result || '');
                    };
                    reader.readAsDataURL(file);
                  }}
                />
              </Button>
              {projectLogoPreview && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Preview:
                  </Typography>
                  <Box
                    component="img"
                    src={projectLogoPreview}
                    alt="Project Logo"
                    sx={{ mt: 0.5, height: 56, borderRadius: 1, border: '1px solid #e0e0e0' }}
                  />
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Project Details
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3} id="field-startDate">
            <TextField
              fullWidth
              required
              type="date"
              label="Start Date"
              value={formData.startDate}
              onChange={(e) => handleFormChange('startDate', e.target.value)}
              onBlur={() => handleFieldBlur('startDate')}
              error={!!errors.startDate}
              helperText={errors.startDate}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Completion Date"
              value={formData.completeDate}
              onChange={(e) => handleFormChange('completeDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="RERA Number"
              value={formData.reraNo}
              onChange={(e) => handleFormChange('reraNo', e.target.value)}
              placeholder="Enter RERA number"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Circle Rate"
              value={formData.guidelineValue}
              onChange={(e) => handleFormChange('guidelineValue', e.target.value)}
              placeholder="₹0.00"
            />
          </Grid>
          {/* <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Reward Points"
              type="number"
              value={formData.rewardPoints + ''}
              onChange={(e) => {
                const val = e.target.value;
                // Only allow digits, allow empty string
                const digitsOnly = val.replace(/\D/g, '');
                if (digitsOnly === '' || (!isNaN(digitsOnly) && parseFloat(digitsOnly) >= 0)) {
                  handleFormChange('rewardPoints', digitsOnly);
                }
              }}
              onKeyDown={(e) => {
                // Prevent negative sign, 'e', 'E', '+'
                if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                  e.preventDefault();
                }
              }}
              placeholder="Enter reward points"
              inputProps={{ min: 0 }}
              sx={{
                '& input[type=number]': {
                  MozAppearance: 'textfield',
                },
                '& input[type=number]::-webkit-outer-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
                '& input[type=number]::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
              }}
            />
          </Grid> */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Latitude"
              value={formData.latitude}
              onChange={(e) => handleFormChange('latitude', e.target.value)}
              placeholder="e.g. 28.6139"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Longitude"
              value={formData.longitude}
              onChange={(e) => handleFormChange('longitude', e.target.value)}
              placeholder="e.g. 77.2090"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Project Description"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              placeholder="Enter a short description of the project"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Amenities
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <FormControl fullWidth>
              <InputLabel>Select Amenities</InputLabel>
              <Select
                multiple
                value={formData.amenities || []}
                onChange={(e) => handleFormChange('amenities', e.target.value)}
                input={<OutlinedInput label="Select Amenities" />}
                renderValue={renderAmenitiesValue}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {amenitiesOptions.map((amenity) => (
                  <MenuItem key={amenity} value={amenity}>
                    <Checkbox checked={(formData.amenities || []).indexOf(amenity) > -1} />
                    <ListItemText primary={amenity} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Commission Details
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={4} id="field-commissionMethod">
            <FormControl fullWidth required error={!!errors.commissionMethod}>
              <InputLabel>Commission Method </InputLabel>
              <Select
                value={formData.commissionMethod || ''}
                onChange={(e) => {
                  handleFormChange('commissionMethod', e.target.value);
                  setErrors((prev) => ({ ...prev, commissionMethod: '' }));
                }}
                label="Commission Method *"
                required
                onBlur={() => handleFieldBlur('commissionMethod')}
              >
                <MenuItem value="">
                  <em>Select commission method</em>
                </MenuItem>
                <MenuItem value="project">Project Commission (%)</MenuItem>
                <MenuItem value="level">Level Commission (%)</MenuItem>
              </Select>
              {errors.commissionMethod && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.commissionMethod}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {formData.commissionMethod === 'project' && (
            <Grid item xs={12} sm={6} md={4} id="field-commission">
              <FormControl fullWidth required error={!!errors.commission}>
                <InputLabel>Commission (%) *</InputLabel>
                <Select
                  value={formData.commission || ''}
                  onChange={(e) => {
                    handleFormChange('commission', e.target.value);
                    setErrors((prev) => ({ ...prev, commission: '' }));
                  }}
                  label="Commission (%) *"
                  required
                  onBlur={() => handleFieldBlur('commission')}
                >
                  <MenuItem value="">
                    <em>Select commission</em>
                  </MenuItem>
                  {commissionOptions.map((percentage) => (
                    <MenuItem key={percentage} value={percentage}>
                      {percentage}%
                    </MenuItem>
                  ))}
                </Select>
                {errors.commission && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.commission}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          )}





          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Address Information
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Full Address"
              value={formData.address}
              onChange={(e) => handleFormChange('address', e.target.value)}
              placeholder="Enter complete address"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} id="field-state">
            <TextField
              fullWidth
              label="State"
              select
              required
              value={formData.state}
              onChange={(e) => handleFormChange('state', e.target.value)}
              onBlur={() => handleFieldBlur('state')}
              error={!!errors.state}
              helperText={errors.state}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            >
              <MenuItem value="">
                <em>Select State</em>
              </MenuItem>
              {states.map((state) => (
                <MenuItem key={state.id} value={state.value}>
                  {state.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4} id="field-city">
            <TextField
              fullWidth
              label="City"
              select
              required
              value={formData.city}
              onChange={(e) => {
                if (e.target.value === "__add_city__") {
                  setAddCityDialogOpen(true);
                  setNewCityName("");
                } else {
                  handleFormChange('city', e.target.value);
                }
              }}
              onBlur={() => handleFieldBlur('city')}
              error={!!errors.city}
              helperText={errors.city || (!formData.state ? "Please select a state first" : "")}
              disabled={!formData.state}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            >
              <MenuItem value="__add_city__" sx={{ color: "#1976d2", fontWeight: 500 }}>
                ➕ Add New City
              </MenuItem>
              <MenuItem value="">
                <em>Select City</em>
              </MenuItem>
              {filteredCities.map((city) => (
                <MenuItem key={city.id} value={city.value}>
                  {city.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Bank Details
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Bank Name"
              select
              value={formData.bankName}
              onChange={(e) => handleFormChange('bankName', e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            >
              <MenuItem value="">
                <em>Select Bank</em>
              </MenuItem>
              {banks.map((bank) => (
                <MenuItem key={bank.id} value={bank.value}>
                  {bank.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Account Holder Name"
              value={formData.accountHolderName}
              onChange={(e) => handleFormChange('accountHolderName', e.target.value)}
              placeholder="Enter account holder name"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Account Number"
              value={formData.accountNumber}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                handleFormChange('accountNumber', value);
                if (accountError) setAccountError("");
              }}
              onBlur={() => {
                if (formData.accountNumber && !validateAccount(formData.accountNumber)) {
                  setAccountError("Account number must be between 9 and 18 digits");
                } else {
                  setAccountError("");
                }
              }}
              inputProps={{ maxLength: 18 }}
              placeholder="Enter account number"
              error={!!accountError}
              helperText={accountError}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="IFSC Code"
              value={formData.ifscCode}
              onChange={(e) => {
                handleFormChange('ifscCode', e.target.value.toUpperCase());
                if (ifscError) setIfscError("");
              }}
              onBlur={() => {
                if (formData.ifscCode && !validateIFSC(formData.ifscCode)) {
                  setIfscError("Invalid IFSC format (e.g. ABCD0123456)");
                } else {
                  setIfscError("");
                }
              }}
              inputProps={{ maxLength: 11 }}
              placeholder="Enter IFSC code"
              error={!!ifscError}
              helperText={ifscError}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>

          {/* Document Management Section */}
          {(
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1976d2' }}>
                  Document Management
                </Typography>
              </Grid>

              {/* Document Form */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Add/Edit Document
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Date"
                        type="date"
                        value={documentForm.date}
                        onChange={(e) => handleDocumentChange('date', e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth required>
                        <InputLabel>Document Type</InputLabel>
                        <Select
                          value={documentForm.documentType}
                          onChange={(e) => handleDocumentChange('documentType', e.target.value)}
                          label="Document Type"
                        >
                          <MenuItem value="">
                            <em>Select type</em>
                          </MenuItem>
                          <MenuItem value="image">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Image fontSize="small" />
                              Image
                            </Box>
                          </MenuItem>
                          <MenuItem value="document">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Description fontSize="small" />
                              Document
                            </Box>
                          </MenuItem>
                          <MenuItem value="video">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <VideoFile fontSize="small" />
                              Video
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Document Title"
                        value={documentForm.documentTitle}
                        onChange={(e) => handleDocumentChange('documentTitle', e.target.value)}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<AttachFile />}
                        sx={{ height: '56px', textTransform: 'none' }}
                        fullWidth
                      >
                        Choose File
                        <input
                          type="file"
                          hidden
                          accept="image/*,video/*,.pdf,.doc,.docx"
                          onChange={handleFileChange}
                        />
                      </Button>
                      {documentForm.file && (
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                          {documentForm.file.name}
                        </Typography>
                      )}
                      {!documentForm.file && documentForm.existingFileName && (
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                          {documentForm.existingFileName}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Document Description"
                        value={documentForm.documentDescription}
                        onChange={(e) => handleDocumentChange('documentDescription', e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        {editingDocumentId && (
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setDocumentForm(getEmptyDocumentForm());
                              setEditingDocumentId(null);
                            }}
                            sx={{ textTransform: 'none' }}
                          >
                            Cancel
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          onClick={handleSaveDocument}
                          startIcon={<Save />}
                          sx={{ textTransform: 'none' }}
                        >
                          {editingDocumentId ? 'Update Document' : 'Save Document'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Documents Table */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Documents ({documents.length})
                  </Typography>
                  {documents.length > 0 ? (
                    <Box sx={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f5f5f5' }}>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>Date</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>Type</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>Mode</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>Title</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>Description</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>File</th>
                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {documents.map((doc) => (
                            <tr key={doc.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '12px' }}>{doc.date}</td>
                              <td style={{ padding: '12px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {doc.type === 'image'
                                    ? <Image fontSize="small" />
                                    : doc.type === 'video'
                                      ? <VideoFile fontSize="small" />
                                      : <Description fontSize="small" />}
                                  {doc.type}
                                </Box>
                              </td>
                              <td style={{ padding: '12px' }}>{doc.mode}</td>
                              <td style={{ padding: '12px' }}>{doc.title}</td>
                              <td style={{ padding: '12px' }}>{doc.description || '-'}</td>
                              <td style={{ padding: '12px' }}>
                                {doc.fileViewUrl ? (
                                  <Button
                                    component="a"
                                    href={doc.fileViewUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    size="small"
                                    sx={{ textTransform: 'none', px: 0, minWidth: 0 }}
                                  >
                                    {doc.fileName || 'View File'}
                                  </Button>
                                ) : doc.fileName ? (
                                  <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {doc.fileName}
                                  </Typography>
                                ) : '-'}
                              </td>
                              <td style={{ padding: '12px', textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => handleEditDocument(doc)}
                                    sx={{ textTransform: 'none', minWidth: 'auto', px: 2 }}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => handleDeleteDocument(doc.id)}
                                    sx={{ textTransform: 'none', minWidth: 'auto', px: 2 }}
                                  >
                                    Delete
                                  </Button>
                                </Box>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                      No documents added yet. Add your first document using the form above.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                onClick={handleFormClose}
                variant="outlined"
                startIcon={<Close />}
                sx={{ textTransform: 'none', px: 4, py: 1.2 }}
                size="large"
              >
                Cancel
              </Button>
              <Button
                onClick={handleFormSubmit}
                variant="contained"
                color="primary"
                startIcon={isEditMode ? <Save /> : <AddIcon />}
                sx={{
                  textTransform: 'none',
                  px: 4,
                  py: 1.2,
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                }}
                size="large"
              >
                {isEditMode ? 'Update Project' : 'Add Project'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Add City Dialog */}
      <Dialog open={addCityDialogOpen} onClose={handleCloseAddCityDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New City</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                disabled
                helperText="City will be added to this state"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="City Name"
                value={newCityName}
                onChange={(e) => setNewCityName(e.target.value)}
                required
                autoFocus
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddCityDialog} disabled={addingCity}>
            Cancel
          </Button>
          <Button
            onClick={handleAddCity}
            variant="contained"
            disabled={!newCityName.trim() || addingCity}
          >
            {addingCity ? "Adding..." : "Add City"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modals */}
      <AlertModal
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        disableRestoreFocus={true}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />

      <ConfirmModal
        open={Boolean(documentDeleteId !== null && documentDeleteId !== undefined)}
        title="Delete Document"
        onClose={() => setDocumentDeleteId(null)}
        onConfirm={confirmDeleteDocument}
        type="error"
        confirmText="Remove"
      >
        Are you sure you want to remove this document?
      </ConfirmModal>
    </Box>
  );
};

export default ProjectForm;
